import { JSX, startTransition } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import DetailFavoriteMobileButton from "@/components/detail/DetailFavoriteMobileButton";
import DetailNotRecMobileButton from "@/components/detail/DetailNotRecMobileButton";
import DetailRateMobileButton from "@/components/detail/DetailRateMobileButton";
import Tag from "@/components/helpers/Tag.tsx";
import { IconPlay, IconStar } from "@/components/icons";
import { IconImdb, IconKinopoisk, IconSlashCircle, IconSplaySmall } from "@/components/icons/main.icons";
import { MODAL_ROUTES } from "@/components/modals/router/ModalRoutes";

import { Banner } from "@/api/content/content.types";
import { ROUTES } from "@/config/routes.ts";
import { useGetContentEpisodes } from "@/hooks/api/content.hooks";
import { useGetEpisodeHistory } from "@/hooks/api/feed.hooks";
import useModalRouter from "@/hooks/useModalRouter";

import Icon from "../Icon/Icon";

type MovieBannerTitleProps = Pick<
  Banner,
  | "age_restrictions"
  | "country"
  | "description"
  | "genres"
  | "logo_image"
  | "year"
  | "rating"
  | "logo_image_rectangle"
  | "imdb_rating"
  | "kinopoisk_rating"
  | "id"
  | "is_serial"
> & {
  firstSeason?: number;
};

const MovieBannerTitleMobile = ({
  age_restrictions,
  country: countries,
  genres,
  logo_image,
  year,
  rating,
  imdb_rating,
  kinopoisk_rating,
  logo_image_rectangle,
  firstSeason = 1,
  is_serial,
  id,
}: MovieBannerTitleProps): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: episodes } = useGetContentEpisodes(+firstSeason);
  const token = localStorage.getItem("splay:token") || "";
  const { data: history } = useGetEpisodeHistory(id);

  const logo = logo_image_rectangle?.small ?? logo_image?.small;
  const { openModal } = useModalRouter();

  const openMovie = () => {
    startTransition(() => {
      if (!token) {
        openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_LOGIN]);
      } else {
        const episodeId = history?.episodes?.[0]?.episode_id || episodes?.episodes?.[0]?.id;
        const route = !is_serial
          ? ROUTES.DETAIL.EPISODE.replace(":id", String(id))
          : !episodes
            ? ROUTES.DETAIL.MAIN.replace(":id", String(id))
            : ROUTES.DETAIL.SERIAL_EPISODE.replace(":id", String(id)).replace(":episodeId", String(episodeId));
        navigate(route);
      }
    });
  };

  const openAuthModal = () => {
    openModal([MODAL_ROUTES.AUTH, MODAL_ROUTES.AUTH_LOGIN]);
  };

  return (
    <div className="absolute bottom-5 left-0 flex w-full justify-center px-4">
      <div className="w-full max-w-[30rem]">
        {logo && (
          <img
            src={logo}
            alt="movie-title"
            loading={"lazy"}
            className={"m-auto mb-4 block h-20 w-auto object-center"}
          />
        )}
        <ul className="flex items-center justify-center gap-4 max-lg:mt-0">
          {rating && (
            <li className="flex items-center gap-[.375rem]">
              <IconSplaySmall className="size-8 fill-orange-1" />
              <span className="text-[24px] font-medium max-lg:text-[14px]"> {rating?.toFixed(1)}</span>
            </li>
          )}
          {kinopoisk_rating && (
            <li className="flex items-center gap-[.375rem]">
              <IconKinopoisk className="size-8" />
              <span className="text-[24px] font-medium max-lg:text-[14px]">{kinopoisk_rating?.toFixed(1)}</span>
            </li>
          )}
          {imdb_rating && (
            <li className="flex items-center gap-[.375rem]">
              <IconImdb className="size-8" />
              <span className="text-[24px] font-medium max-lg:text-[14px]">{imdb_rating?.toFixed(1)}</span>
            </li>
          )}
        </ul>
        <ul
          className={
            "mt-2 flex h-[1.875rem] flex-wrap items-center justify-center gap-[.625rem] overflow-hidden max-lg:gap-2"
          }
        >
          {year && (
            <li>
              <Link to={ROUTES.MAIN.YEAR.replace(":year", year.toString())}>
                <Tag
                  type={"square"}
                  className={
                    "!border-[0px] bg-surface-container-high  hover:bg-orange-1 max-xl:px-2 max-xl:py-1.5 max-xl:text-[12px]"
                  }
                >
                  {year.toString()}
                </Tag>
              </Link>
            </li>
          )}
          {countries && (
            <li>
              <Tag
                type={"square"}
                className={
                  "text-nowrap !border-[0px] bg-surface-container-high  hover:bg-orange-1 max-xl:px-2 max-xl:py-1.5 max-xl:text-[12px]"
                }
              >
                {countries.slice(0, 2).map((country, index) => (
                  <Link key={country.id} to={ROUTES.MAIN.COUNTRY.replace(":id", String(country.id))}>
                    {country.name} {index === 0 && countries.length > 1 ? " / " : ""}
                  </Link>
                ))}
              </Tag>
            </li>
          )}
          {genres && (
            <li>
              <Tag
                type={"square"}
                className={
                  "text-nowrap !border-[0px] bg-surface-container-high  hover:bg-orange-1 max-xl:px-2 max-xl:py-1.5 max-xl:text-[12px]"
                }
              >
                {genres.slice(0, 2).map((genre, index) => (
                  <Link to={ROUTES.MAIN.GENRE.replace(":id", String(genre.id))} key={genre.id}>
                    {genre.name} {index === 0 && genres.length > 1 ? " / " : ""}
                  </Link>
                ))}
              </Tag>
            </li>
          )}
          <li>
            <Tag
              type={"square"}
              className={
                "!border-[0px] bg-surface-container-high  hover:bg-orange-1 max-xl:px-2 max-xl:py-1.5 max-xl:text-[12px]"
              }
            >
              +{age_restrictions}
            </Tag>
          </li>
        </ul>
        <Button
          icon={<IconPlay className={"size-6 fill-white max-md:h-[18px] max-md:w-[14px]"} />}
          onClick={openMovie}
          title={t("Actions.start-watching")}
          className="my-4 py-[15px] text-[16px]"
        >
          {(history?.time_code || history?.episodes?.length || 0) > 0
            ? t("Actions.continue-watching")
            : t("Actions.start-watching")}
        </Button>
        {token ? (
          <div className="grid w-full grid-cols-3 pt-3">
            <DetailNotRecMobileButton id={id} />
            <DetailRateMobileButton id={id} />
            <DetailFavoriteMobileButton id={id} />
          </div>
        ) : (
          <div className="grid w-full grid-cols-3 pt-3">
            <div className="group flex flex-col items-center gap-2" onClick={openAuthModal}>
              <IconSlashCircle className="size-6 stroke-on-primary" />
              <p className="text-[14px] text-on-primary">{t("Actions.not-interesting")}</p>
            </div>
            <div className="group flex flex-col items-center gap-2" onClick={openAuthModal}>
              <IconStar className="size-6 stroke-[#fff]" />
              <p className="text-[14px]">{t("Actions.estimate")}</p>
            </div>
            <div className="group flex flex-col items-center gap-2" onClick={openAuthModal}>
              <Icon name={"bookmark-simple"} className={"size-6 fill-transparent stroke-white"} />
              <p className="text-[14px]">{t("Actions.save")}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MovieBannerTitleMobile;
